import React from 'react'
import { useId } from 'react'
import { MaterialSymbol } from 'react-material-symbols'

import { Tooltip } from 'primereact/tooltip'

import { DeliveryForecastProcessCompletions } from '../../features/selfservice/supplier/data'
import i18n from '../../utils/i18n'

type Props = {
  icon: React.ReactNode
  tooltipText: string
}

const CustomIconWithTooltip: React.FC<Props> = ({ icon, tooltipText }) => {
  // Remove the ":" in the id because this is not valid
  const removeColons = (str: string): string => str.replace(/^:|:$/g, '')

  // Give every tooltip an unique id because otherwise they
  // would add up behind and create a very dark shadow
  const tooltipId = removeColons(useId())
  const tooltipSelector = `#${tooltipId}`

  return (
    <>
      <Tooltip target={tooltipSelector} />
      <i
        id={tooltipId}
        className="custom-target-icon p-text-secondary p-overlay-badge cursor-pointer"
        data-pr-tooltip={tooltipText}
        data-pr-position="top"
        data-pr-at="center top-20"
      >
        {icon}
      </i>
    </>
  )
}

export const DelForProcessBodyTemplate = (
  processStep: DeliveryForecastProcessCompletions,
  size: number = 16,
) => {
  if (!processStep) return null

  const createIcon = (icon: React.ReactNode, tooltipText: string) => (
    <CustomIconWithTooltip icon={icon} tooltipText={tooltipText} />
  )

  switch (processStep.toLowerCase() as DeliveryForecastProcessCompletions) {
    case 'confirmation':
      return (
        <div className="flex align-items-center">
          {createIcon(
            <MaterialSymbol icon="calendar_month" size={size} />,
            i18n.t('common.tableTemplates.supplierProcess.deliveryCallOff'),
          )}

          <MaterialSymbol icon="chevron_right" size={size} />

          {createIcon(
            <MaterialSymbol icon="task_alt" size={size} />,
            i18n.t('common.tableTemplates.supplierProcess.deliveryForecast'),
          )}
        </div>
      )
    case 'despatchadvice':
      return (
        <div className="flex align-items-center">
          {createIcon(
            <MaterialSymbol icon="calendar_month" size={size} />,
            i18n.t('common.tableTemplates.supplierProcess.deliveryCallOff'),
          )}

          <MaterialSymbol icon="chevron_right" size={size} />

          {createIcon(
            <MaterialSymbol icon="local_shipping" size={size} />,
            i18n.t(
              'common.tableTemplates.supplierProcess.despatchAdviceWithConfirmation',
            ),
          )}
        </div>
      )
    case 'despatchadvice_with_confirmation':
      return (
        <div className="flex align-items-center">
          {createIcon(
            <MaterialSymbol icon="calendar_month" size={size} />,
            i18n.t('common.tableTemplates.supplierProcess.deliveryCallOff'),
          )}

          <MaterialSymbol icon="chevron_right" size={size} />

          {createIcon(
            <MaterialSymbol icon="task_alt" size={size} />,
            i18n.t('common.tableTemplates.supplierProcess.deliveryForecast'),
          )}

          <MaterialSymbol icon="chevron_right" size={size} />

          {createIcon(
            <MaterialSymbol icon="local_shipping" size={size} />,
            i18n.t(
              'common.tableTemplates.supplierProcess.despatchAdviceWithConfirmation',
            ),
          )}
        </div>
      )
    default:
      return null
  }
}
